// Section 7 — "Fill with the no-model accounts"
// (explains-page-contract.json s6_no_model_fill): under-target need vs
// what each no-model account fills, the sequential pool progression,
// scale factors, and collapsible per-account residual baskets.
import { Card, Table } from "react-bootstrap";
import { formatCurrency } from "../../../../lib/numbers";
import type {
  AccountRecord,
  CompletionResidualModel,
} from "../../lib/explainTypes";
import type { SoftSectionContext } from "./SoftCompletionContext";
import {
  AC_PALETTE,
  CHART_COLORS,
  ChartLegend,
  HBarChart,
  buildColorScale,
} from "./charts";
import {
  CASH_SYMBOL,
  assetClassOf,
  groupByAssetClass,
  num,
  residualAccountIds,
} from "./softCompletionLib";

interface FillAccount {
  accountId: number;
  account: AccountRecord | undefined;
  model: CompletionResidualModel | undefined;
  balance: number;
  // What the account actually buys per asset class (final_target ×
  // balance, cash excluded).
  fillByAc: Record<string, number>;
  // Total invested (non-cash) fill.
  fillTotal: number;
}

function fillAccounts(ctx: SoftSectionContext): FillAccount[] {
  return residualAccountIds(ctx.completion).map((accountId) => {
    const account = ctx.accountsById.get(accountId);
    const model = ctx.completion.residual_models?.[String(accountId)];
    const balance = num(account?.balance);
    const finalTarget = Object.fromEntries(
      Object.entries(
        account?.constrained_allocation?.final_target ?? {},
      ).filter(([symbol]) => symbol !== CASH_SYMBOL),
    );
    const fillByAc = Object.fromEntries(
      Object.entries(groupByAssetClass(finalTarget, ctx.symbolToAc)).map(
        ([ac, w]) => [ac, w * balance],
      ),
    );
    return {
      accountId,
      account,
      model,
      balance,
      fillByAc,
      fillTotal: Object.values(fillByAc).reduce((a, b) => a + b, 0),
    };
  });
}

export default function NoModelFillSection({
  ctx,
}: {
  ctx: SoftSectionContext;
}) {
  const { completion, resolveAccountName } = ctx;
  const accounts = fillAccounts(ctx);
  if (accounts.length === 0) return null;

  const accountColor = buildColorScale(
    accounts.map((a) => String(a.accountId)),
  );

  // Need at stage-7 start: the positive raw residuals faced by the
  // first no-model account in order.
  const needByAc = Object.fromEntries(
    Object.entries(accounts[0].model?.raw_residuals_by_asset_class ?? {}).map(
      ([ac, v]) => [ac, num(v)],
    ),
  );
  const needRows = Object.entries(needByAc)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);

  // Left unfilled after the last account.
  const progression = completion.constrained_pool_progression ?? [];
  const lastResidual =
    progression[progression.length - 1]?.residual_by_asset_class;
  const remainingByAc = Object.fromEntries(
    Object.entries(lastResidual ?? {}).map(([ac, v]) => [
      ac,
      Math.max(num(v), 0),
    ]),
  );
  const remainingTotal = Object.values(remainingByAc).reduce(
    (a, b) => a + b,
    0,
  );
  const needTotal = needRows.reduce((acc, [, v]) => acc + v, 0);
  const filledTotalAll = accounts.reduce((acc, a) => acc + a.fillTotal, 0);

  return (
    <Card className="mb-3">
      <Card.Header>Fill with the no-model accounts</Card.Header>
      <Card.Body>
        {needRows.length > 0 && (
          <>
            <HBarChart
              title="Under-target need vs what the pool can fill (residual_models · final targets · pool progression)"
              rows={needRows.map(([ac, need]) => {
                const filled = accounts.reduce(
                  (acc, a) => acc + (a.fillByAc[ac] ?? 0),
                  0,
                );
                const remaining = remainingByAc[ac] ?? 0;
                return {
                  label: ac,
                  sub: `need ${formatCurrency(need)}`,
                  segments: [
                    ...accounts.map((a) => ({
                      value: a.fillByAc[ac] ?? 0,
                      color: accountColor(String(a.accountId)),
                      title: `${resolveAccountName(a.accountId)} ${formatCurrency(a.fillByAc[ac] ?? 0)}`,
                    })),
                    {
                      value: remaining,
                      color: CHART_COLORS.pale,
                      title: `remaining ${formatCurrency(remaining)}`,
                    },
                  ],
                  note:
                    filled > 0
                      ? `filled ${formatCurrency(filled)} · left ${formatCurrency(remaining)}`
                      : `unfilled ${formatCurrency(remaining)}`,
                };
              })}
            />
            <ChartLegend
              items={[
                ...accounts.map((a) => ({
                  label: `${resolveAccountName(a.accountId)} (${formatCurrency(a.balance)})`,
                  color: accountColor(String(a.accountId)),
                })),
                { label: "unfillable remainder", color: CHART_COLORS.pale },
              ]}
            />

            <Table size="sm" responsive className="mt-2 mb-0">
              <caption className="text-muted small">
                How much of the need each account closes
              </caption>
              <thead>
                <tr>
                  <th>Asset class</th>
                  <th className="text-end">Need</th>
                  {accounts.map((a) => (
                    <th key={a.accountId} className="text-end">
                      {resolveAccountName(a.accountId)}
                    </th>
                  ))}
                  <th className="text-end">Filled</th>
                  <th className="text-end">Remaining</th>
                </tr>
              </thead>
              <tbody>
                {needRows.map(([ac, need]) => {
                  const filled = accounts.reduce(
                    (acc, a) => acc + (a.fillByAc[ac] ?? 0),
                    0,
                  );
                  return (
                    <tr key={ac}>
                      <td>{ac}</td>
                      <td className="text-end">{formatCurrency(need)}</td>
                      {accounts.map((a) => (
                        <td key={a.accountId} className="text-end">
                          {(a.fillByAc[ac] ?? 0) > 0
                            ? formatCurrency(a.fillByAc[ac])
                            : "—"}
                        </td>
                      ))}
                      <td className="text-end">
                        {filled > 0 ? formatCurrency(filled) : "—"}
                      </td>
                      <td
                        className={`text-end ${
                          (remainingByAc[ac] ?? 0) > 0 ? "text-danger" : ""
                        }`}
                      >
                        {formatCurrency(remainingByAc[ac] ?? 0)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="fw-bold">
                  <td>Total</td>
                  <td className="text-end">{formatCurrency(needTotal)}</td>
                  {accounts.map((a) => (
                    <td key={a.accountId} className="text-end">
                      {formatCurrency(a.fillTotal)}
                    </td>
                  ))}
                  <td className="text-end">{formatCurrency(filledTotalAll)}</td>
                  <td className="text-end text-danger">
                    {formatCurrency(remainingTotal)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </>
        )}

        <HBarChart
          title="Sequential fill — the need each account faces at its turn (constrained_pool_progression)"
          rows={[
            ...accounts.map((a) => {
              const faced = Object.values(
                a.model?.raw_residuals_by_asset_class ?? {},
              ).reduce((acc, v) => acc + Math.max(num(v), 0), 0);
              return {
                label: `${resolveAccountName(a.accountId)} faces`,
                sub: `${formatCurrency(a.balance)} account`,
                segments: [{ value: faced, color: AC_PALETTE[4] }],
                note: `${formatCurrency(faced)} · fills ${formatCurrency(a.fillTotal)}`,
              };
            }),
            {
              label: "left unfilled",
              segments: [{ value: remainingTotal, color: CHART_COLORS.pale }],
              note: formatCurrency(remainingTotal),
            },
          ]}
        />

        <Table size="sm" responsive className="mt-2 mb-0">
          <caption className="text-muted small">
            Scale factors — residual target scaled to the account&apos;s
            capacity
          </caption>
          <thead>
            <tr>
              <th>Account</th>
              <th>Scaled</th>
              <th className="text-end">× factor</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.accountId}>
                <td>{resolveAccountName(a.accountId)}</td>
                <td>{a.model?.scaled ? "Yes" : "No"}</td>
                <td className="text-end">
                  {a.model?.scale_factor != null
                    ? num(a.model.scale_factor).toFixed(4)
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <details className="mt-3">
          <summary
            className="fw-bold"
            style={{ cursor: "pointer", fontSize: "0.85rem" }}
          >
            Residual baskets, security level (residual_target_weights ·
            checkpoints · final_target)
          </summary>
          {accounts.map((a) => (
            <BasketTable key={a.accountId} ctx={ctx} fill={a} />
          ))}
        </details>
      </Card.Body>
    </Card>
  );
}

// One no-model account's basket: current holdings → residual target →
// post → change, per symbol, with a balance-conserving total row.
function BasketTable({
  ctx,
  fill,
}: {
  ctx: SoftSectionContext;
  fill: FillAccount;
}) {
  const { resolveAccountName, symbolToAc } = ctx;
  const balance = fill.balance;
  const holdings = fill.account?.checkpoints?.holdings?.by_symbol ?? {};
  const residualWeights = fill.model?.residual_target_weights ?? {};
  const finalTarget = fill.account?.constrained_allocation?.final_target ?? {};
  const symbols = [
    ...new Set([
      ...Object.keys(holdings),
      ...Object.keys(residualWeights),
      ...Object.keys(finalTarget),
    ]),
  ];
  const rows = symbols
    .map((symbol) => {
      const current = num(holdings[symbol]) * balance;
      const residualTarget =
        residualWeights[symbol] != null
          ? num(residualWeights[symbol]) * balance
          : null;
      const post = num(finalTarget[symbol]) * balance;
      return {
        symbol,
        ac: symbol === CASH_SYMBOL ? "cash" : assetClassOf(symbol, symbolToAc),
        current,
        residualTarget,
        post,
        change: post - current,
      };
    })
    .filter((r) => r.current !== 0 || r.post !== 0 || r.residualTarget != null)
    .sort((a, b) => b.post - a.post || b.current - a.current);
  if (rows.length === 0) return null;

  const totals = rows.reduce(
    (acc, r) => ({
      current: acc.current + r.current,
      post: acc.post + r.post,
      change: acc.change + r.change,
    }),
    { current: 0, post: 0, change: 0 },
  );

  return (
    <Table size="sm" responsive className="mt-2 mb-0">
      <caption className="text-muted small">
        {resolveAccountName(fill.accountId)} — {formatCurrency(balance)}
        {fill.model?.scale_factor != null
          ? ` · scaled ×${num(fill.model.scale_factor).toFixed(4)}`
          : ""}
      </caption>
      <thead>
        <tr>
          <th>Symbol</th>
          <th>Asset class</th>
          <th className="text-end">Current</th>
          <th className="text-end">Residual target</th>
          <th className="text-end">Post</th>
          <th className="text-end">Change</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.symbol}>
            <td>{r.symbol}</td>
            <td>{r.ac === "unknown" ? "cash" : r.ac}</td>
            <td className="text-end">
              {r.current !== 0 ? formatCurrency(r.current) : "—"}
            </td>
            <td className="text-end">
              {r.residualTarget != null
                ? formatCurrency(r.residualTarget)
                : "—"}
            </td>
            <td className="text-end">
              {r.post !== 0 ? formatCurrency(r.post) : "—"}
            </td>
            <td
              className={`text-end ${
                r.change > 0
                  ? "text-success"
                  : r.change < 0
                    ? "text-danger"
                    : ""
              }`}
            >
              {r.change === 0
                ? "—"
                : `${r.change > 0 ? "+" : "−"}${formatCurrency(Math.abs(r.change))}`}
            </td>
          </tr>
        ))}
        <tr className="fw-bold">
          <td>Total</td>
          <td></td>
          <td className="text-end">{formatCurrency(totals.current)}</td>
          <td className="text-end"></td>
          <td className="text-end">{formatCurrency(totals.post)}</td>
          <td className="text-end">
            {Math.abs(totals.change) < 1
              ? "—"
              : `${totals.change > 0 ? "+" : "−"}${formatCurrency(Math.abs(totals.change))}`}
          </td>
        </tr>
      </tbody>
    </Table>
  );
}
